import { Resources } from 'constants/resources';

import { yupResolver } from '@hookform/resolvers/yup';
import { useGoBack } from 'hooks/useGoBack';
import { PresetsItemsSection } from 'pages/Presets/components/PresetsItemsSection/PresetsItemsSection';
import { useEditPresets } from 'pages/Presets/hooks/useEditPresets';
import { PresetItemFormValue } from 'pages/Presets/PresetsCreate/PresetsCreateForm/PresetsCreateForm';
import { PRESETS_EDIT_ITEMS_SCHEMA } from 'pages/Presets/schemas/schema';
import { mapPresetItemToFormValue } from 'pages/Presets/utils/mapPresetItemToFormValue';
import { queryClient } from 'providers/queryClient';
import { Form, useNotify } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { CancelButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { EditPage } from 'shared/react-admin/Pages/EditPage/EditPage';

import { PresetsEditItemsSkeleton } from './PresetsEditItemsSkeleton';

type Props = {
  apiKey: 'payment_items' | 'payout_items';
  directionId: string;
  itemsField: 'paymentItems' | 'payoutItems';
  title: string;
  successText: string;
};

export const PresetsEditItems: React.FC<Props> = ({
  apiKey,
  directionId,
  itemsField,
  title,
  successText,
}) => {
  const notify = useNotify();
  const { goBack } = useGoBack();

  const { handleSubmit, error, record } = useEditPresets({
    direction: apiKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [Resources.Presets] });
      notify(successText, { type: 'info' });
      goBack();
    },
  });

  const items = record?.[itemsField] ?? [];
  const defaultValues = {
    items: items.map((item) => mapPresetItemToFormValue(item, directionId)),
  };

  return (
    <EditPage listName="Пресеты" title={title}>
      {!record ? (
        <PresetsEditItemsSkeleton />
      ) : (
        <Form
          defaultValues={defaultValues}
          key={record?.id}
          onSubmit={(data: FieldValues) => handleSubmit(data as { items: PresetItemFormValue[] })}
          resolver={yupResolver<FieldValues>(PRESETS_EDIT_ITEMS_SCHEMA)}
        >
          <Box sx={{ pt: '24px' }}>
            <PresetsItemsSection direction={directionId} error={error} name="items" />
          </Box>
          <CardActionsButtons
            rightActionsSlot={[
              <CancelButton key="cancel" linkType="back" variant="outlined" />,
              <SaveButton key="save" label="Сохранить" />,
            ]}
            sx={{ padding: 0 }}
          />
        </Form>
      )}
    </EditPage>
  );
};
